import { saveUpdateContent, CONTENT_STORE_KEYS } from '../../../../../../context-controller-mod/context-serv/global-provider/local-storage-context/updateContentStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/contentSectionEditPageSale.css';
import Cookies from 'js-cookie';
import ApiGet    from '../../../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPut    from '../../../../../../api-controller-mod/api-serv/put-api/putApi';
import ApiDelete from '../../../../../../api-controller-mod/api-serv/delete-api/deleteApi';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import TextField from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField';
import { Button, Confirmation, Toggle } from '@core/uilibrary';
import { useGlobalPopupProvider } from '../../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { DocConfirmationIcon, SuccessDocConfirmationIcon, WarningConfirmationIcon } from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import EditContentBreadcrumb from '../../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/edit-content-breadcrumb/ts/editContentBreadcrumb';

import { SaleContactSection, SlugResponse, UpdateEntry } from './types';
import { BASE_PATH, BREADCRUMB_PATH } from './constants';
import SaleEditor from './saleEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const ContentSectionEditPageSale: React.FC = () => {
  const { contentType, sectionId, contentUuid: paramContentUuid, contentVersionUuid: paramContentVersionUuid } = useParams<{ contentType: string; sectionId: string; contentUuid: string; contentVersionUuid: string }>();
  const navigate        = useNavigate();
  const { showToast }   = useToast();
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();

  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [contentUuid,    setContentUuid]    = useState(paramContentUuid ?? '');
  const [metaStatus,     setMetaStatus]     = useState('PUBLISHED');
  const [section,        setSection]        = useState<SaleContactSection | null>(null);
  const [sectionVisible, setSectionVisible] = useState(true);

  const userId             = Cookies.get('userUuid')           ?? '';
  const username           = Cookies.get('username')            ?? '';
  const contentVersionUuid = paramContentVersionUuid ?? Cookies.get('contentVersionUuid') ?? '';

  const isSavedRef  = useRef(false);
  const origTitle   = useRef<string>('');
  const origAuthor  = useRef<string>('');
  const origSection = useRef<SaleContactSection | null>(null);
  const origStatus  = useRef<string>('PUBLISHED');

  // ── Fetch by slug ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    ApiGet<SlugResponse>(
      `${API_ENDPOINTS.GET_CMS_CONTENT}?slug=saleContact`
    )
      .then(res => {
        if (cancelled) return;
        const resData = res?.data?.data;
        const cta     = resData?.data?.saleContact?.cta?.[0];
        if (!cta) return;

        const sec = cta.saleContactSection;

        // Never overwrite a UUID that came from the URL param
        setContentUuid(prev => prev || String(resData.contentId ?? ''));

        setSection(sec);
        setMetaStatus(resData.status ?? 'PUBLISHED');
        origStatus.current = resData.status ?? 'PUBLISHED';
        setSectionVisible(sec?.visible ?? true);

        origSection.current = sec;
        origTitle.current   = sec?.content[0]?.headerSection?.title ?? '';
        origAuthor.current  = username;
      })
      .catch(err => { if (!cancelled) console.error('[SaleSectionEdit] fetch:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  // ── Delete draft ───────────────────────────────────────────────────────────
  /**
   * isDelete logic (mirrors product page):
   *  - key missing        → call delete API + remove cookie
   *  - isDelete === false → skip delete API, remove only isDelete key
   *  - isDelete === true  → call delete API + remove cookie
   */
  const deleteDraft = useCallback(async () => {
    if (!contentVersionUuid) return;

    const raw      = localStorage.getItem('isDelete');
    const isDelete = raw !== null
      ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
      : null;

    // Explicitly false → skip API, just clean up the flag
    if (isDelete === false) {
      localStorage.removeItem('isDelete');
      return;
    }

    // Missing or true → delete the draft
    localStorage.removeItem('isDelete');
    try {
      await ApiDelete(
        `${API_ENDPOINTS.DELETE_DRAFT_CONTENT}?version=${contentVersionUuid}`
      );
    } catch (err) {
      console.warn('[SaleSectionEdit] delete draft failed:', err);
    } finally {
      Cookies.remove('contentVersionUuid');
    }
  }, [contentVersionUuid]);

  // ── On unmount ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (!isSavedRef.current) {
        deleteDraft();
      } else {
        localStorage.removeItem('isDelete');
      }
    };
  }, [deleteDraft]);

  // ── Build path-based updates ───────────────────────────────────────────────
  const buildUpdates = useCallback((nextStatus: string): UpdateEntry[] => {
  const updates:      UpdateEntry[] = [];
  const fieldUpdates: UpdateEntry[] = [];
  if (!section || !origSection.current) return updates;

  // ── Section visible — always send ──────────────────────────────────────
  fieldUpdates.push({
    path:  ['saleContact', 'cta', 0, 'saleContactSection', 'visible'],
    value: sectionVisible,
  });

  const currHeader = section.content[0]?.headerSection;
  const origHeader = origSection.current.content[0]?.headerSection;
  const currCrumb  = section.content[0]?.breadCrumbSection;
  const origCrumb  = origSection.current.content[0]?.breadCrumbSection;

  if (currHeader && origHeader) {
    // Header visible — always send
    fieldUpdates.push({
      path:  [...BASE_PATH, 'visible'],
      value: currHeader.visible,
    });

    if (currHeader.title !== origHeader.title)
      fieldUpdates.push({ path: [...BASE_PATH, 'title'],       value: currHeader.title });
    if (currHeader.description !== origHeader.description)
      fieldUpdates.push({ path: [...BASE_PATH, 'description'], value: currHeader.description });

    const listsChanged = currHeader.lists.some((item, idx) => {
      const o = origHeader.lists[idx];
      return !o || item.title !== o.title || item.label !== o.label;
    });
    if (listsChanged)
      fieldUpdates.push({ path: [...BASE_PATH, 'lists'], value: currHeader.lists });
  }

  if (currCrumb && origCrumb) {
    // Breadcrumb visible — always send
    fieldUpdates.push({
      path:  [...BREADCRUMB_PATH, 'visible'],
      value: currCrumb.visible,
    });

    if (currCrumb.breadCrumbTitleSt !== origCrumb.breadCrumbTitleSt)
      fieldUpdates.push({ path: [...BREADCRUMB_PATH, 'breadCrumbTitleSt'], value: currCrumb.breadCrumbTitleSt });
    if (currCrumb.breadCrumbTitleNd !== origCrumb.breadCrumbTitleNd)
      fieldUpdates.push({ path: [...BREADCRUMB_PATH, 'breadCrumbTitleNd'], value: currCrumb.breadCrumbTitleNd });
  }

  // Single status push — always included alongside fieldUpdates
  updates.push(...fieldUpdates);
  updates.push({
    path:  ['saleContact', 'cta', 0, 'saleContactSection', 'status'],
    value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT',
  });

  return updates;
}, [section, sectionVisible]);

  // ── Save ───────────────────────────────────────────────────────────────────
  const performSave = useCallback(async () => {
    setSaving(true);
    try {
      await ApiPut(
        `${API_ENDPOINTS.UPDATE_CONTENT_BY_PATH}?contentUuid=${contentUuid}&userId=${userId}&versionUuid=${contentVersionUuid}`,
        { type: 'page', title: 'Sale Contact Page', version: 1, updates: buildUpdates(metaStatus) },
      );
      isSavedRef.current = true;
      saveUpdateContent(
        CONTENT_STORE_KEYS.SALE,
        contentType ?? 'sale', contentUuid, contentVersionUuid, 'saleContactSection',
      );
      localStorage.removeItem('isDelete');
      Cookies.remove('contentVersionUuid');
      showToast({ status: 'success', title: 'Complete !',   description: 'Section was updated successfully.' });
      navigate(`/workflow/manage/preview/${contentUuid}/${contentVersionUuid}/draft`);
    } catch (err) {
      console.error('[SaleSectionEdit] save:', err);
      showToast({ status: 'error', title: 'Uncomplete !', description: 'Section was updated unsuccessfully.' });
    } finally {
      setSaving(false);
    }
  }, [buildUpdates, metaStatus, contentUuid, userId, contentVersionUuid, contentType, navigate, showToast]);

  // ── Confirm save ───────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Save changes"
        description='Are you sure you want to save changes to the "Sale Contact" section? This will update the live content.'
        status="info" icon={<SuccessDocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"   size="md" value="Cancel" type="button" onClick={closeConfirm} />
          <Button variant="primary" size="md" value="Save"   type="button"
            onClick={() => { closeConfirm(); performSave(); }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, performSave]);

  // ── Confirm cancel ─────────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Discard changes"
        description="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        status="warning" icon={<DocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"  size="md" value="Cancel"    type="button" onClick={closeConfirm} />
          <Button variant="danger" size="md" value="Discard" type="button"
            onClick={async () => {
              closeConfirm();
              await deleteDraft();
              isSavedRef.current = true;
              localStorage.removeItem('isDelete');
              navigate(`/content/${contentType ?? 'sale'}`);
            }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, navigate, contentType, deleteDraft]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="scl--cms-content-edit-sale-root">
        <div className="scl--cms-content-edit-sale-skeleton-wrap">
          <div className="scl--cms-content-edit-sale-skeleton scl--cms-content-edit-sale-skeleton--title" />
          <div className="scl--cms-content-edit-sale-skeleton scl--cms-content-edit-sale-skeleton--desc"  />
          <div className="scl--cms-content-edit-sale-skeleton scl--cms-content-edit-sale-skeleton--field" />
          <div className="scl--cms-content-edit-sale-skeleton scl--cms-content-edit-sale-skeleton--field" />
          <div className="scl--cms-content-edit-sale-skeleton scl--cms-content-edit-sale-skeleton--field" />
        </div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="scl--cms-content-edit-sale-root">
        <div className="scl--cms-content-edit-sale-empty-state">
          <p className="scl--cms-content-edit-sale-empty-title">Section not found</p>
          <p className="scl--cms-content-edit-sale-empty-desc">Could not load sale contact section.</p>
          <Button variant="ghost" value="← Back"
            onClick={() => navigate(`/content/${contentType ?? 'sale'}`)} />
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="scl--cms-content-edit-sale-root">
      <EditContentBreadcrumb
        onHomeClick={() => { localStorage.removeItem('isDelete'); navigate('/overview'); }}
        onContentClick={() => { localStorage.removeItem('isDelete'); navigate('/content/sale'); }}
      />

      <h1 className="scl--cms-content-edit-sale-page-title">Edit section</h1>
      <p className="scl--cms-content-edit-sale-page-desc">
        Responsible for managing and maintaining website content to ensure accuracy.
      </p>

      <div className="scl--cms-content-edit-sale-meta-grid">
        <TextField label="Section ID" value="SC-001"              disabled />
        <TextField label="Author"     value={origAuthor.current} disabled />
        <TextField label="Section"    value="Sale Contact"      disabled />
        <TextField label="Title"      value={origTitle.current} disabled />
      </div>

      <div className="scl--cms-content-edit-sale-section-editor">
        <div className="scl--cms-content-edit-sale-section-heading-row">
          <h2 className="scl--cms-content-edit-sale-section-heading">Sale contact</h2>
          <Toggle checked={sectionVisible} onChange={setSectionVisible} />
        </div>
        <div className="scl--cms-content-edit-sale-divider" />

        <SaleEditor data={section} onChange={setSection} />
      </div>

      <div className="scl--cms-content-edit-sale-actions">
        <Button variant="ghost"   value="Cancel"                      onClick={handleCancel} />
        <Button variant="primary" value={saving ? 'Saving…' : 'Save'} onClick={handleSave}  disabled={saving} />
      </div>
    </div>
  );
};

export default ContentSectionEditPageSale;